/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/**
* Test if a value is an array-like object containing only nonnegative integers.
*
* @module @stdlib/assert-is-nonnegative-integer-array
*
* @example
* import isNonNegativeIntegerArray from '@stdlib/assert-is-nonnegative-integer-array';
*
* var bool = isNonNegativeIntegerArray( [ 3.0, new Number(3.0) ] );
* // returns true
*
* bool = isNonNegativeIntegerArray( [ 3.0, '3.0' ] );
* // returns false
*
* @example
* import { primitives as isNonNegativeIntegerArray } from '@stdlib/assert-is-nonnegative-integer-array';
*
* var bool = isNonNegativeIntegerArray( [ 1.0, 0.0, 10.0 ] );
* // returns true
*
* bool = isNonNegativeIntegerArray( [ 3.0, new Number(1.0) ] );
* // returns false
*
* @example
* import { objects as isNonNegativeIntegerArray } from '@stdlib/assert-is-nonnegative-integer-array';
*
* var bool = isNonNegativeIntegerArray( [ new Number(3.0), new Number(1.0) ] );
* // returns true
*
* bool = isNonNegativeIntegerArray( [ 1.0, 0.0, 10.0 ] );
* // returns false
*/

// MODULES //

import isNonNegativeInteger from '@stdlib/assert-is-nonnegative-integer';
import setReadOnly from '@stdlib/utils-define-nonenumerable-read-only-property';
import arrayfun from '@stdlib/assert-tools-array-like-function';


// VARIABLES //

var isPrimitiveArray = arrayfun( isNonNegativeInteger.isPrimitive );
var isObjectArray = arrayfun( isNonNegativeInteger.isObject );


// MAIN //

var isNonNegativeIntegerArray = arrayfun( isNonNegativeInteger );
setReadOnly( isNonNegativeIntegerArray, 'primitives', isPrimitiveArray );
setReadOnly( isNonNegativeIntegerArray, 'objects', isObjectArray );


// EXPORTS //

export default isNonNegativeIntegerArray;
